/**
 * `OD-37`, round 7 (the agent's R4, data half), workflow v3 Step 0 (financing request) and section 3.6, the
 * founder's apport rulings of 8 October: **where a buyer's deposit may come from, and what is never accepted as
 * one.** The API's dossier domain and the client's opening step read this one text, so the screen refuses on the
 * spot exactly what the door will refuse , and the founder's sentence is spelled once.
 *
 * - The origin is a closed list of five. Cash, mobile money and money paid to the dealership are NOT in it: the
 *   deposit must be on a formal bank account in the buyer's name.
 * - Four origins ask for one free line (a third party's name, link and reason; which activity; which asset; what
 *   else). Savings ask for none.
 * - A free line that names a forbidden way is refused too: the list could be walked around in words.
 *
 * Pure: no IO, no clock.
 */
export const DEPOSIT_ORIGINS = [
  "epargne_constituee",
  "virement_ou_don_tiers",
  "revenu_activite",
  "vente_bien",
  "autre",
] as const;
export type DepositOrigin = (typeof DEPOSIT_ORIGINS)[number];

/** The values a buyer might try that are not origins , refused in the founder's sentence. */
export const FORBIDDEN_DEPOSIT_VALUES: readonly string[] = [
  "especes",
  "mobile_money",
  "verse_au_concessionnaire",
];

/** The founder's sentence, word for word (the catalogue's `client.apport.refusCompteFormel` carries the same). */
export const FORMAL_ACCOUNT_SENTENCE = "L'apport doit se trouver sur un compte bancaire formel à votre nom";

/** The origins that need the free line, and what each line is asked to say. */
export const DEPOSIT_ORIGINS_NEEDING_A_LINE: readonly DepositOrigin[] = [
  "virement_ou_don_tiers",
  "revenu_activite",
  "vente_bien",
  "autre",
];

export const DEPOSIT_DETAIL_MAX_LENGTH = 240;

export const isDepositOrigin = (value: unknown): value is DepositOrigin =>
  typeof value === "string" && (DEPOSIT_ORIGINS as readonly string[]).includes(value);

export const originNeedsALine = (origin: DepositOrigin): boolean =>
  DEPOSIT_ORIGINS_NEEDING_A_LINE.includes(origin);

const plain = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/** « espèces », « argent liquide », « cash »; « mobile money », « Orange Money », « Moov Money », « Wave ». */
const CASH_OR_MOBILE = /\b(?:especes?|cash|liquide|mobile\s*money|orange\s*money|moov\s*money|wave)\b/u;
/** Money handed to the dealership: a verb of payment, a few words, then « concessionnaire ». */
const PAID_TO_THE_DEALER =
  /\b(?:vers[ea]e?s?|paye[e]?s?|remis[e]?s?|donne[e]?s?|depose[e]?s?|regle[e]?s?)\b(?:\s+\S+){0,4}\s+(?:au|chez\s+le|a\s+l[ae]?)\s+concessionnaire\b/u;

/** Does a free line name a way of paying a deposit that is not a formal account? */
export function namesAForbiddenDepositWay(line: string): boolean {
  const text = plain(line);
  return CASH_OR_MOBILE.test(text) || PAID_TO_THE_DEALER.test(text);
}

/** What the free line is asked to say, per origin, for the screen and for the door's refusal. */
export const DEPOSIT_LINE_ASKS: Readonly<Record<DepositOrigin, string>> = {
  epargne_constituee: "",
  virement_ou_don_tiers: "Indiquez le nom de la personne, votre lien avec elle et le motif de son aide.",
  revenu_activite: "Indiquez laquelle de vos activités vous a procuré cet apport.",
  vente_bien: "Indiquez quel bien vous avez vendu.",
  autre: "Précisez d'où vient cet apport.",
};
